import * as Sentry from '@sentry/nextjs';
import type { AxiosError } from 'axios';
import { type ClassValue, clsx } from 'clsx';
import { marked } from 'marked';
import { twMerge } from './twMerge';

import type { ProblemDetails } from '@/open-api/model';
import { logEvent } from '@/pages/api/v1/utils';
import { GenericErrorCode, RecaptchaErrorCode } from '@/types/errors';
import {
  trackPurchase,
  trackSharedBoardSubscriptionConversion,
} from '@/utils/analytics';
import { clearConversion, getConversion } from '@/utils/analytics/conversion';
import { refreshMetaEventId } from '@/utils/analytics/facebook/metaEventId';
import { addGAEvent } from '@/utils/analytics/google/addGAEvent';

import { SHARED_BOARD_REFERRAL_SESSION_KEY } from '@/features/Boards/constants';
import type { SubscriptionType } from '@/types/subscription';

let gtagReadyPromise: Promise<void> | null = null;

const createGtagReadyPromise = (): Promise<void> =>
  new Promise((resolve) => {
    if (typeof window === 'undefined') {
      return resolve();
    }

    const isLoaded = () =>
      window.dataLayer?.some(
        (item) =>
          item !== null &&
          typeof item === 'object' &&
          (item as Record<string, unknown>).event === 'gtm.load'
      );

    if (isLoaded()) {
      return resolve();
    }

    if (!window.dataLayer) {
      return resolve();
    }

    const originalPush = window.dataLayer.push.bind(window.dataLayer);

    const timeoutId = setTimeout(() => {
      window.dataLayer.push = originalPush;
      resolve();
    }, 3000);

    window.dataLayer.push = (...args: unknown[]) => {
      const result = originalPush(...args);
      const item = args[0];

      if (
        item !== null &&
        typeof item === 'object' &&
        (item as Record<string, unknown>).event === 'gtm.load'
      ) {
        clearTimeout(timeoutId);
        window.dataLayer.push = originalPush;
        resolve();
      }

      return result;
    };
  });

const waitForGtag = (): Promise<void> => {
  if (!gtagReadyPromise) {
    gtagReadyPromise = createGtagReadyPromise();
  }
  return gtagReadyPromise;
};

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const stripMarkdown = (markdown: string): string => {
  const html = marked.parse(markdown, {
    async: false,
  });

  return html.replace(/<\/?[^>]+(>|$)/g, '').trim();
};

/**
 * Type guard to check if a number is a valid RecaptchaErrorCode
 */
export function isRecaptchaErrorCode(code: number): code is RecaptchaErrorCode {
  return Object.values(RecaptchaErrorCode).includes(code);
}

/**
 * Type guard to check if a number is a valid GenericErrorCode
 */
export function isGenericErrorCode(code: number): code is GenericErrorCode {
  return Object.values(GenericErrorCode).includes(code);
}

/**
 * Get a user-friendly error message for a recaptcha error code
 * @param code The recaptcha error code received from the backend
 * @returns A user-friendly error message for recaptcha errors
 */
export function getRecaptchaErrorMessage(code: number): string {
  if (!isRecaptchaErrorCode(code)) {
    return 'An unknown error has occurred';
  }

  switch (code) {
    case RecaptchaErrorCode.Empty:
      return 'reCAPTCHA response empty';

    case RecaptchaErrorCode.NotSuccessful:
      return 'reCAPTCHA validation not successful';

    case RecaptchaErrorCode.ScoreLow:
      return 'reCAPTCHA v3 score too low';

    case RecaptchaErrorCode.Failed:
      return 'reCAPTCHA validation failed';

    default:
      return 'An unknown error has occurred';
  }
}

export function getGenericErrorMessage(code: GenericErrorCode): string {
  if (!isGenericErrorCode(code)) {
    return 'An unknown error has occurred';
  }

  switch (code) {
    case GenericErrorCode.OutstandingPayment:
      return 'You currently have an outstanding payment on your subscription';

    case GenericErrorCode.ExpiredFreeSubscription:
      return 'Your free subscription has expired';

    case GenericErrorCode.NoValidSubscription:
      return 'No valid subscription found for your premium account';

    case GenericErrorCode.FairUsageLimitExceeded:
      return 'You have exceeded your fair usage limit';

    case GenericErrorCode.InactiveSubscription:
      return 'Your premium subscription is currently inactive';

    case GenericErrorCode.PausedSubscription:
      return 'Your premium subscription is currently paused';

    case GenericErrorCode.PaymentNotFound:
      return "We couldn't find a payment for your premium subscription";

    case GenericErrorCode.UnverifiedEmail:
      return 'Please verify your email address';

    case GenericErrorCode.TrackNotLive:
      return 'This track is currently not available';

    case GenericErrorCode.PremiumTrackWithFreeAccount:
      return 'This track is only available with a premium subscription';

    case GenericErrorCode.DownloadsExhausted:
      return 'You have used all your available downloads';

    case GenericErrorCode.UnknownError:
    default:
      return 'An unknown error has occurred';
  }
}

/**
 * Helper to get error message from a numeric code
 */
function getMessageFromCode(code: number): string | null {
  if (isRecaptchaErrorCode(code)) {
    return getRecaptchaErrorMessage(code);
  }
  if (isGenericErrorCode(code)) {
    return getGenericErrorMessage(code);
  }
  return null;
}

/**
 * Get a user-friendly error message for a given error code or error object
 * @param codeOrError The error code or error object received from the backend
 * @param fallbackMessage Optional fallback message when no error code is found
 * @returns A user-friendly error message
 */
export function getErrorMessage(
  codeOrError:
    | number
    | string
    | { code?: string | number; error_code?: string | number; message?: string }
    | undefined,
  fallbackMessage?: string
): string {
  // Extract code from error object
  let code: number | string | undefined;
  let backendMessage: string | undefined;

  if (typeof codeOrError === 'object' && codeOrError !== null) {
    // Support both 'code' and 'error_code' field names
    code = codeOrError.code ?? codeOrError.error_code;
    backendMessage = codeOrError.message;
  } else {
    code = codeOrError;
  }

  // Try to convert code to number and get message
  if (code !== undefined) {
    const numericCode =
      typeof code === 'string' ? Number.parseInt(code, 10) : code;
    if (!Number.isNaN(numericCode)) {
      const message = getMessageFromCode(numericCode);
      if (message) {
        return message;
      }
    }
  }

  // Fall back to backend message or default
  if (backendMessage?.trim()) {
    return backendMessage;
  }

  return fallbackMessage || 'An unknown error has occurred';
}

/**
 * The `ProblemDetails.title` string the V2 download endpoints return when a
 * user has hit their fair-usage limit. The backend maps the eligibility reason
 * onto the RFC-7807 `title` field (via `Results.Problem(title: ...)`) and has
 * no machine-readable error code for this case, so we match on the title string
 * — kept here as a single source of truth.
 */
export const FAIR_USAGE_TITLE = 'Fair usage limit exceeded';

/**
 * Detect a fair-usage 403 thrown by the Orval-generated download fetchers.
 * The V2 download endpoints return HTTP 403 with a `ProblemDetails` body whose
 * `title` is {@link FAIR_USAGE_TITLE}, so the fetcher rejects with an
 * `AxiosError` rather than returning an error object. Matching on the title
 * (rather than the status alone) distinguishes fair-usage from the other 403s
 * the endpoints raise, e.g. "Insufficient privileges".
 *
 * Matches on the `isAxiosError` flag rather than `axios.isAxiosError()` so this
 * shared module (pulled into most UI bundles via `cn`) keeps a type-only Axios
 * dependency and doesn't drag the Axios runtime into every bundle.
 */
export const isFairUsageError = (error: unknown): boolean => {
  if (typeof error !== 'object' || error === null) {
    return false;
  }

  const axiosError = error as AxiosError<ProblemDetails>;

  return (
    axiosError.isAxiosError === true &&
    axiosError.response?.status === 403 &&
    axiosError.response.data?.title === FAIR_USAGE_TITLE
  );
};

/**
 * Get the first string that is not undefined or an empty string from an
 * infinite number of parameters, as well as has a length greater than 0 when
 * trimmed. This is similar to nullish coalescing but it will also check if
 * the string is empty.
 *
 * @param strings Any number of strings
 * @returns The first string that is not undefined or an empty string
 */
export function getFirstString(
  ...strings: (string | undefined | null)[]
): string {
  return (
    strings.find(
      (string) =>
        string !== undefined &&
        string !== null &&
        string !== '' &&
        string.trim().length > 0
    ) ?? ''
  );
}

const INVISIBLE_FORMAT_CHARACTERS = /\p{Cf}/gu;

const cleanHeading = (heading?: string) =>
  (heading ?? '').replace(INVISIBLE_FORMAT_CHARACTERS, '').trim();

export const getCategoryPageTitle = ({
  primary_heading_visitor,
  primary_heading,
  name,
  userIsAuthenticated,
}: {
  primary_heading_visitor?: string;
  primary_heading?: string;
  name?: string;
  userIsAuthenticated: boolean;
}) => {
  const firstString = getFirstString(
    cleanHeading(primary_heading),
    cleanHeading(name)
  );

  if (!userIsAuthenticated) {
    const visitorHeading = cleanHeading(primary_heading_visitor);

    if (visitorHeading) {
      return visitorHeading;
    }

    return `Free ${firstString}`;
  }

  return firstString;
};

export const isAssetNew = (setLiveUnixTimestamp: number) => {
  if (setLiveUnixTimestamp === 0) {
    return false;
  }

  const sevenDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
  const setLiveDate = new Date(setLiveUnixTimestamp * 1000); // Convert Unix timestamp (seconds) to milliseconds

  return setLiveDate.getTime() > sevenDaysAgo;
};

export const getFreeDownloadsText = (credits: number) => {
  if (credits === 1) {
    return '1 free download';
  }

  return `${credits} free downloads`;
};

// Convert a string to a hash
export const stringToHash = (str: string) => {
  let hash = 0;

  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash &= hash;
  }

  return Math.abs(hash);
};
/* eslint-enable no-bitwise, no-plusplus */

// Get a color from a string
export const getColorFromString = (str: string) => {
  const hash = stringToHash(str);
  const hue = hash % 360;
  const color = `hsl(${hue}, 70%, 40%)`;

  return color;
};

/**
 * Console log a message with a timestamp and context, only log if the
 * environment is not production, using the NEXT_PUBLIC_NODE_ENV environment
 * variable.
 *
 * @param message The message to log
 * @param context The context of the message
 */
export const log = (...args: unknown[]) => {
  if (process.env.NODE_ENV !== 'production' && typeof window !== 'undefined') {
    const { pathname } = window.location;
    const color = getColorFromString(pathname);

    // biome-ignore lint/suspicious/noConsole: Intentional dev-only logging utility
    console.log(
      `[${new Date().toLocaleString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        fractionalSecondDigits: 3,
      })}] %c[${pathname}]`,
      `color: ${color}`,
      ...args
    );
  }
};

/**
 * Format a time in seconds to a string in the format "mm:ss"
 * @param timeInSeconds The time in seconds to format
 * @returns The formatted time
 */
export const formatTime = (timeInSeconds: number) => {
  const minutes = Math.floor(timeInSeconds / 60);
  const seconds = Math.floor(timeInSeconds % 60);

  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

/**
 * Track a conversion across multiple channels
 * @param subscriptionType The type of subscription
 * @param subscriptionLength The length of the subscription
 * @param planType The type of plan
 * @param paymentType The type of payment
 * @param asPath The path of the page
 * @param currencyCode The currency code
 * @param value The value of the conversion
 */
export const trackConversion = async ({
  subscriptionType,
  subscriptionLength,
  paymentType,
  asPath,
  currencyCode,
  value,
}: {
  subscriptionType: SubscriptionType;
  subscriptionLength: 'Annual' | 'Monthly';
  paymentType: 'card' | 'paypal';
  asPath: string;
  currencyCode: string;
  value: number;
}) => {
  let success = false;
  let error: Error | null = null;

  try {
    log('Tracking conversion', {
      subscriptionType,
      subscriptionLength,
      paymentType,
      asPath,
      currencyCode,
      value,
    });

    if (sessionStorage.getItem(SHARED_BOARD_REFERRAL_SESSION_KEY)) {
      trackSharedBoardSubscriptionConversion();
      sessionStorage.removeItem(SHARED_BOARD_REFERRAL_SESSION_KEY);
    }

    const {
      conversion_source,
      conversion_path,
      conversion_asset_type,
      conversion_asset_id,
    } = getConversion();

    // Flush now: the success-screen CTA appears right after this and a
    // hard navigation discards the 5s client batch (DEV-6834).
    await logEvent(
      {
        event_type: 'purchase',
        asPath,
        metadata: {
          payment_type: paymentType as string,
          plan_type: subscriptionType,
          plan_length: subscriptionLength,
          currency: currencyCode,
          value,
          conversion_source,
          conversion_path,
          conversion_asset_type,
          conversion_asset_id,
        },
      },
      true
    );

    clearConversion();

    void waitForGtag()
      .then(() => {
        trackPurchase(subscriptionType, subscriptionLength);
        addGAEvent(`purchase-${paymentType}`);
        refreshMetaEventId();
      })
      .catch((e) => {
        console.warn('Error sending GA4 events', e);
      });

    success = true;
  } catch (e) {
    console.warn('Error tracking purchase', e);

    Sentry.captureException('Error tracking purchase', {
      extra: {
        error: e,
        paymentType,
        subscriptionType,
        subscriptionLength,
        asPath,
        currencyCode,
        value,
      },
    });

    error = e as Error;
  }

  return { success, error };
};

export { generateUUID } from '@/utils/uuid';

/**
 * Get the currency symbol for a given locale and currency
 * @param locale The locale to use
 * @param currency The currency to use
 * @returns The currency symbol
 */
export const getCurrencySymbol = (locale: string, currency: string) =>
  (0)
    .toLocaleString(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
    .replace(/\d/g, '')
    .trim();
